/* Ruoli di TUTTI i membri: salvarli (anche su file), toglierli a tutti (es. manutenzione) e rimetterli. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, Eraser, Loader2, RotateCcw, Save, Trash2, Upload } from "lucide-react";
import { apiError, botDelete, botGet, botPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputC, outlineBtn, primaryBtn } from "@/components/kit";
import { InfoTip, RolePicker } from "@/pages/tickets/ticketsKit";

const mr = {
  list: () => botGet("member-roles/snapshots"),
  save: (name) => botPost("member-roles/snapshots", { name }),
  get: (id) => botGet(`member-roles/snapshots/${id}`),
  remove: (id) => botDelete(`member-roles/snapshots/${id}`),
  upload: (file) => botPost("member-roles/import", file),
  strip: (body) => botPost("member-roles/strip", body),
  apply: (id, body) => botPost(`member-roles/snapshots/${id}/apply`, body),
  job: (id) => botGet(`member-roles/jobs/${id}`),
};
const LAST_GIVE = "kalyx-last-maintenance-role";
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const SOURCE = { manual: ["Manuale", "#5865F2"], auto: ["Automatico", "#80848E"], file: ["Da file", "#F47FFF"] };

async function download(id) {
  const snap = await mr.get(id);
  const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `ruoli-membri-${new Date(snap.at).toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 30000);
}

const OneRole = ({ value, onChange, placeholder }) => (
  <RolePicker value={value ? [value] : []} onChange={(ids) => onChange(ids[ids.length - 1] ?? null)} placeholder={value ? "Cambia ruolo" : placeholder} />
);

/** Barra di avanzamento di un'operazione del bot. */
function JobProgress({ job }) {
  if (!job) return null;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 100;
  const done = job.status !== "running";
  return (
    <div className={`space-y-2 rounded-xl border p-3 ${done ? "border-[#23A55A]/40 bg-[#23A55A]/10" : "border-[#5865F2]/40 bg-[#5865F2]/10"}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-white">
        {done ? <CheckCircle2 className="h-4 w-4 text-[#57F287]" /> : <Loader2 className="h-4 w-4 animate-spin text-[#8ea0ff]" />}
        {done ? "Fatto!" : "Lavoro in corso… puoi lasciare aperta questa finestra"}
        <span className="ml-auto font-mono text-xs text-[#B5BAC1]">{job.done}/{job.total}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-black/30"><div className="h-full rounded-full bg-[#5865F2] transition-all" style={{ width: `${pct}%` }} /></div>
      <p className="text-xs text-[#B5BAC1]">Membri modificati: <b className="text-white">{job.changed}</b>{job.errors ? <> · errori: <b className="text-[#ff9a9c]">{job.errors}</b></> : null}
        {job.notInServer ? <> · non più nel server: {job.notInServer}</> : null}</p>
      {job.missingRoles?.length > 0 && <p className="text-xs text-[#FEE75C]">Ruoli del salvataggio che non esistono più (saltati): {job.missingRoles.join(", ")}</p>}
      {job.lastError && <p className="text-xs text-[#ff9a9c]">Ultimo errore: {job.lastError}</p>}
    </div>
  );
}

function useJob(onDone) {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const follow = (j) => {
    setJob(j);
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const x = await mr.job(j.id);
        setJob(x);
        if (x.status !== "running") {
          clearInterval(timer.current);
          toast.success(`Fatto: ${x.changed} membri modificati${x.errors ? `, ${x.errors} errori` : ""}`);
          onDone?.();
        }
      } catch { clearInterval(timer.current); }
    }, 1000);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return [job, follow, () => setJob(null)];
}

// ─────────────────────────── 1. salva ───────────────────────────
function SaveDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  useEffect(() => { if (open) { setName(""); setSaved(null); } }, [open]);
  const save = async () => {
    setBusy(true);
    try { setSaved(await mr.save(name)); toast.success("Ruoli di tutti i membri salvati"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Salva i ruoli di tutti</DialogTitle>
          <DialogDescription className="text-[#949BA4]">Il bot si segna quali ruoli ha ogni membro, così puoi rimetterli quando vuoi. Puoi anche scaricarlo come file da tenere sul PC.</DialogDescription>
        </DialogHeader>
        {saved ? (
          <div className="space-y-3">
            <p className="rounded-xl bg-[#23A55A]/10 p-3 text-sm text-[#b8f0cc]">✓ Salvato «{saved.name}»: {saved.members} membri, {saved.withRoles} con almeno un ruolo.</p>
            <Button onClick={() => download(saved.id)} className={`w-full ${primaryBtn}`}><Download className="mr-2 h-4 w-4" />Scarica il file</Button>
          </div>
        ) : (
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome (facoltativo) — es. «prima della manutenzione»" className={inputC} maxLength={100} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className={outlineBtn}>{saved ? "Chiudi" : "Annulla"}</Button>
          {!saved && <Button onClick={save} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salva</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── 2. togli a tutti ───────────────────────────
function StripDialog({ open, onOpenChange, onDone }) {
  const [give, setGive] = useState(null);
  const [keep, setKeep] = useState([]);
  const [skipAdmins, setSkipAdmins] = useState(false);
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [job, follow, reset] = useJob(onDone);
  useEffect(() => {
    if (open) { setSure(false); reset(); try { setGive(localStorage.getItem(LAST_GIVE) || null); } catch { /* niente */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const start = async () => {
    setBusy(true);
    try {
      const j = await mr.strip({ giveRoleId: give, keepRoleIds: keep, skipAdmins });
      try { if (give) localStorage.setItem(LAST_GIVE, give); } catch { /* niente */ }
      follow(j);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (job?.status === "running" ? null : onOpenChange(o))}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Togli i ruoli a tutti</DialogTitle>
          <DialogDescription className="text-[#949BA4]">Toglie a ogni membro tutti i ruoli che il bot può gestire. <b className="text-white">Prima salva in automatico i ruoli di tutti</b>, così con «Rimetti i ruoli» torna tutto com'era.</DialogDescription>
        </DialogHeader>
        {job ? <JobProgress job={job} /> : (
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Dai a tutti questo ruolo<InfoTip text="Facoltativo. Ad esempio «Manutenzione»: dopo aver tolto i ruoli, il bot dà a tutti questo ruolo. Quando rimetti i ruoli puoi toglierlo a tutti in automatico." /></p>
              <OneRole value={give} onChange={setGive} placeholder="+ Nessuno (es. Manutenzione)" />
            </div>
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Ruoli da NON togliere<InfoTip text="Facoltativo. Chi ha questi ruoli li tiene (es. i ruoli dello staff, così possono continuare a lavorare durante la manutenzione)." /></p>
              <RolePicker value={keep} onChange={setKeep} placeholder="+ Aggiungi ruolo da lasciare" />
            </div>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-[#232838] bg-[#131620] px-3 py-2.5 text-sm">
              <span className="flex items-center gap-1.5">Non toccare gli amministratori<InfoTip text="Se acceso, chi ha il permesso «Amministratore» tiene tutti i suoi ruoli. Vale anche per il proprietario del server." /></span>
              <Switch checked={skipAdmins} onCheckedChange={setSkipAdmins} />
            </label>
            <p className="text-xs text-[#949BA4]">Non vengono mai toccati: i bot, i ruoli dei bot e delle integrazioni (es. «Server Booster») e i ruoli più in alto di KALYX BOT.</p>
            <label className="flex items-start gap-2 rounded-xl border border-[#F23F43]/40 bg-[#F23F43]/10 p-3 text-sm text-[#ffc1c2]">
              <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#F23F43]" />
              <span><AlertTriangle className="mr-1 inline h-4 w-4" />Ho capito: i membri perderanno i loro ruoli finché non li rimetto.</span>
            </label>
          </div>
        )}
        <DialogFooter>
          {job ? <Button onClick={() => onOpenChange(false)} disabled={job.status === "running"} className={primaryBtn}>Chiudi</Button> : <>
            <Button variant="outline" onClick={() => onOpenChange(false)} className={outlineBtn}>Annulla</Button>
            <Button onClick={start} disabled={!sure || busy} className="bg-[#F23F43] font-semibold text-white hover:bg-[#d83236]" data-testid="strip-start">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eraser className="mr-2 h-4 w-4" />}Togli i ruoli a tutti
            </Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── 3. rimetti / importa ───────────────────────────
function RestoreDialog({ open, onOpenChange, onDone }) {
  const [list, setList] = useState(null);
  const [pick, setPick] = useState(null);
  const [removeIds, setRemoveIds] = useState([]);
  const [mode, setMode] = useState("exact");
  const [busy, setBusy] = useState(false);
  const [job, follow, reset] = useJob(onDone);
  const fileRef = useRef(null);
  const load = () => mr.list().then((r) => { setList(r.snapshots); return r.snapshots; }).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    if (!open) return;
    reset(); setPick(null); setMode("exact");
    try { const g = localStorage.getItem(LAST_GIVE); setRemoveIds(g ? [g] : []); } catch { setRemoveIds([]); }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const upload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const data = JSON.parse(reader.result);
        const s = await mr.upload(data);
        await load();
        setPick(s.id);
        toast.success(`File caricato: ${s.members} membri`);
      } catch (e) { toast.error(e instanceof SyntaxError ? "Il file non è valido" : apiError(e)); } finally { if (fileRef.current) fileRef.current.value = ""; }
    };
    reader.readAsText(file);
  };
  const remove = async (id) => {
    try { await mr.remove(id); if (pick === id) setPick(null); await load(); } catch (e) { toast.error(apiError(e)); }
  };
  const start = async () => {
    setBusy(true);
    try { follow(await mr.apply(pick, { removeRoleIds: removeIds, mode })); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (job?.status === "running" ? null : onOpenChange(o))}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-white">Rimetti i ruoli</DialogTitle>
          <DialogDescription className="text-[#949BA4]">Scegli un salvataggio (o carica un file) e il bot ridà a ogni membro i ruoli che aveva. Prima salva in automatico la situazione attuale.</DialogDescription>
        </DialogHeader>
        {job ? <JobProgress job={job} /> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Salvataggi</p>
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className={`h-8 ${outlineBtn}`}><Upload className="mr-1.5 h-3.5 w-3.5" />Carica un file</Button>
            </div>
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {!list && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#949BA4]" /></div>}
              {list?.length === 0 && <p className="rounded-xl border border-dashed border-[#232838] p-4 text-center text-sm text-[#949BA4]">Nessun salvataggio: usa «Salva ruoli di tutti» o carica un file.</p>}
              {list?.map((s) => {
                const [label, tone] = SOURCE[s.source] ?? SOURCE.manual;
                return (
                  <div key={s.id} onClick={() => setPick(s.id)} role="button"
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 ${pick === s.id ? "border-[#5865F2] bg-[#5865F2]/15" : "border-[#232838] bg-[#131620] hover:border-[#3a4160]"}`}>
                    <span className={`h-4 w-4 shrink-0 rounded-full border-2 ${pick === s.id ? "border-[#5865F2] bg-[#5865F2]" : "border-[#4b4d54]"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{s.name}</p>
                      <p className="text-[11px] text-[#949BA4]"><span className="mr-1.5 rounded px-1.5 py-px text-[10px] font-bold text-white" style={{ background: tone }}>{label}</span>{fmt(s.at)} · {s.members} membri · {s.withRoles} con ruoli</p>
                    </div>
                    <button type="button" title="Scarica il file" onClick={(e) => { e.stopPropagation(); download(s.id).catch((x) => toast.error(apiError(x))); }} className="rounded-md p-1.5 text-[#949BA4] hover:bg-white/5 hover:text-white"><Download className="h-4 w-4" /></button>
                    <button type="button" title="Elimina" onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="rounded-md p-1.5 text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
            {pick && <>
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Togli a tutti questi ruoli<InfoTip text="Ad esempio «Manutenzione»: alla fine viene tolto a tutti (tranne a chi lo aveva già nel salvataggio). Se l'hai usato in «Togli i ruoli a tutti» è già inserito." /></p>
                <RolePicker value={removeIds} onChange={setRemoveIds} placeholder="+ Aggiungi ruolo da togliere" />
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {[["exact", "Esattamente come prima", "I ruoli tornano identici al salvataggio: quelli aggiunti dopo vengono tolti."], ["add", "Solo aggiungi i mancanti", "Ridà i ruoli del salvataggio senza togliere quelli che hanno adesso."]].map(([k, t, d]) => (
                  <button key={k} type="button" onClick={() => setMode(k)} className={`rounded-xl border p-3 text-left ${mode === k ? "border-[#5865F2] bg-[#5865F2]/10" : "border-[#232838] bg-[#131620]"}`}>
                    <p className="text-sm font-semibold text-white">{t}</p><p className="text-xs text-[#949BA4]">{d}</p>
                  </button>
                ))}
              </div>
            </>}
          </div>
        )}
        <DialogFooter>
          {job ? <Button onClick={() => onOpenChange(false)} disabled={job.status === "running"} className={primaryBtn}>Chiudi</Button> : <>
            <Button variant="outline" onClick={() => onOpenChange(false)} className={outlineBtn}>Annulla</Button>
            <Button onClick={start} disabled={!pick || busy} className="bg-[#23A55A] font-semibold text-white hover:bg-[#1e8e4d]" data-testid="restore-start">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}Rimetti i ruoli
            </Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── i tre tasti ───────────────────────────
export default function MemberRolesTools({ onDone }) {
  const [open, setOpen] = useState(null);
  const btn = "rounded-xl bg-white/5 text-white hover:bg-white/10";
  return (
    <>
      <Button onClick={() => setOpen("save")} className={btn} data-testid="mr-save"><Save className="mr-2 h-4 w-4 text-[#8ea0ff]" />Salva ruoli di tutti</Button>
      <Button onClick={() => setOpen("strip")} className={btn} data-testid="mr-strip"><Eraser className="mr-2 h-4 w-4 text-[#ff9a9c]" />Togli i ruoli a tutti</Button>
      <Button onClick={() => setOpen("restore")} className={btn} data-testid="mr-restore"><RotateCcw className="mr-2 h-4 w-4 text-[#57F287]" />Rimetti / Importa ruoli</Button>
      <SaveDialog open={open === "save"} onOpenChange={(o) => setOpen(o ? "save" : null)} />
      <StripDialog open={open === "strip"} onOpenChange={(o) => setOpen(o ? "strip" : null)} onDone={onDone} />
      <RestoreDialog open={open === "restore"} onOpenChange={(o) => setOpen(o ? "restore" : null)} onDone={onDone} />
    </>
  );
}
